import React, { useState, useCallback } from 'react';
import { TrendingDown, Search, AlertCircle, Loader, Zap } from 'lucide-react';

// Stock universe - moved inside component to avoid dependency issues
const STOCK_UNIVERSE = [
  // Tier 1: High volatility mega/large caps
  { symbol: 'TSLA', name: 'Tesla Inc.', tier: 1 },
  { symbol: 'NVDA', name: 'NVIDIA Corp.', tier: 1 },
  { symbol: 'META', name: 'Meta Platforms Inc.', tier: 1 },
  { symbol: 'NFLX', name: 'Netflix Inc.', tier: 1 },
  { symbol: 'AMD', name: 'Advanced Micro Devices', tier: 1 },
  { symbol: 'COIN', name: 'Coinbase Global', tier: 1 },
  { symbol: 'ROKU', name: 'Roku Inc.', tier: 1 },
  { symbol: 'SNAP', name: 'Snap Inc.', tier: 1 },
  { symbol: 'UBER', name: 'Uber Technologies', tier: 1 },
  { symbol: 'SPOT', name: 'Spotify Technology', tier: 1 },
  
  // Tier 2: FAANG + Stable mega caps
  { symbol: 'AAPL', name: 'Apple Inc.', tier: 2 },
  { symbol: 'MSFT', name: 'Microsoft Corp.', tier: 2 },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', tier: 2 },
  { symbol: 'AMZN', name: 'Amazon.com Inc.', tier: 2 },
  { symbol: 'GOOG', name: 'Alphabet Inc. Class A', tier: 2 },
  
  // Tier 3: Growth stocks with dip potential
  { symbol: 'PLTR', name: 'Palantir Technologies', tier: 3 },
  { symbol: 'SQ', name: 'Block Inc.', tier: 3 },
  { symbol: 'PYPL', name: 'PayPal Holdings Inc.', tier: 3 },
  { symbol: 'SHOP', name: 'Shopify Inc.', tier: 3 },
  { symbol: 'ZM', name: 'Zoom Video Communications', tier: 3 },
  { symbol: 'DOCU', name: 'DocuSign Inc.', tier: 3 },
  { symbol: 'TWLO', name: 'Twilio Inc.', tier: 3 },
  { symbol: 'OKTA', name: 'Okta Inc.', tier: 3 },
  { symbol: 'SNOW', name: 'Snowflake Inc.', tier: 3 },
  { symbol: 'CRWD', name: 'CrowdStrike Holdings', tier: 3 },
  
  // Tier 4: Traditional large caps
  { symbol: 'JPM', name: 'JPMorgan Chase & Co.', tier: 4 },
  { symbol: 'V', name: 'Visa Inc.', tier: 4 },
  { symbol: 'MA', name: 'Mastercard Inc.', tier: 4 },
  { symbol: 'UNH', name: 'UnitedHealth Group Inc.', tier: 4 },
  { symbol: 'HD', name: 'Home Depot Inc.', tier: 4 },
  { symbol: 'PG', name: 'Procter & Gamble Co.', tier: 4 },
  { symbol: 'JNJ', name: 'Johnson & Johnson', tier: 4 },
  { symbol: 'BAC', name: 'Bank of America Corp.', tier: 4 },
  { symbol: 'WMT', name: 'Walmart Inc.', tier: 4 },
  { symbol: 'DIS', name: 'Walt Disney Co.', tier: 4 },
  
  // Tier 5: More S&P 500 with volatility
  { symbol: 'CRM', name: 'Salesforce Inc.', tier: 5 },
  { symbol: 'ADBE', name: 'Adobe Inc.', tier: 5 },
  { symbol: 'INTC', name: 'Intel Corp.', tier: 5 },
  { symbol: 'CSCO', name: 'Cisco Systems Inc.', tier: 5 },
  { symbol: 'PFE', name: 'Pfizer Inc.', tier: 5 },
  { symbol: 'KO', name: 'Coca-Cola Co.', tier: 5 },
  { symbol: 'PEP', name: 'PepsiCo Inc.', tier: 5 },
  { symbol: 'ABBV', name: 'AbbVie Inc.', tier: 5 },
  { symbol: 'TMO', name: 'Thermo Fisher Scientific', tier: 5 },
  { symbol: 'COST', name: 'Costco Wholesale Corp.', tier: 5 },
  
  // Additional high-potential dip stocks
  { symbol: 'F', name: 'Ford Motor Co.', tier: 6 },
  { symbol: 'GM', name: 'General Motors Co.', tier: 6 },
  { symbol: 'BA', name: 'Boeing Co.', tier: 6 },
  { symbol: 'GE', name: 'General Electric Co.', tier: 6 },
  { symbol: 'T', name: 'AT&T Inc.', tier: 6 },
  { symbol: 'VZ', name: 'Verizon Communications', tier: 6 }
];

const DipDetectApp = () => {
  const [minDrop, setMinDrop] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [scanComplete, setScanComplete] = useState(false);

  const fetchStockData = async (stock) => {
    try {
      const response = await fetch(`/.netlify/functions/stock-data?symbol=${stock.symbol}`);

      if (!response.ok) return null;

      // Capture cache headers
      const cacheStatus = response.headers.get('X-Cache');
      const cacheAge = response.headers.get('X-Cache-Age');

      const data = await response.json();

      if (data.error || data['Error Message'] || data['Note']) return null;

      const timeSeries = data['Time Series (Daily)'];
      if (!timeSeries) return null;

      const dates = Object.keys(timeSeries).sort().reverse();
      if (dates.length < 2) return null;

      const today = dates[0];
      const yesterday = dates[1];
      const todayData = timeSeries[today];
      const yesterdayData = timeSeries[yesterday];

      const currentPrice = parseFloat(todayData['4. close']);
      const previousClose = parseFloat(yesterdayData['4. close']);
      const change = currentPrice - previousClose;
      const changePercent = (change / previousClose) * 100;
      const volume = parseInt(todayData['5. volume']);

      // Calculate 52-week metrics
      const last52Weeks = dates.slice(0, Math.min(252, dates.length));
      let high52w = 0;
      let low52w = Infinity;

      last52Weeks.forEach(date => {
        const dayHigh = parseFloat(timeSeries[date]['2. high']);
        const dayLow = parseFloat(timeSeries[date]['3. low']);
        if (dayHigh > high52w) high52w = dayHigh;
        if (dayLow < low52w) low52w = dayLow;
      });

      const dipFrom52w = ((currentPrice - high52w) / high52w) * 100;

      return {
        symbol: stock.symbol,
        name: stock.name,
        tier: stock.tier,
        price: currentPrice,
        change: change,
        changePercent: changePercent,
        volume: volume,
        high52w: high52w,
        low52w: low52w,
        dipFrom52w: dipFrom52w,
        lastUpdated: today,
        cached: cacheStatus === 'HIT',
        cacheAge: cacheAge ? parseInt(cacheAge) : null
      };
    } catch (error) {
      return null;
    }
  };

  const scanMarket = useCallback(async () => {
    setLoading(true);
    setError(null);
    setStockData([]);
    setScanComplete(false);
    
    // Prioritize by tier, scan 50 stocks for good coverage
    const stocksToScan = STOCK_UNIVERSE.slice(0, 50);
    setProgress({ loaded: 0, total: stocksToScan.length });
    
    const results = [];
    const batchSize = 3;
    
    try {
      for (let i = 0; i < stocksToScan.length; i += batchSize) {
        const batch = stocksToScan.slice(i, i + batchSize);
        
        // Process batch in parallel
        const batchPromises = batch.map(stock => fetchStockData(stock));
        const batchResults = await Promise.all(batchPromises);
        
        // Add successful results
        batchResults.forEach(result => {
          if (result) {
            results.push(result);
            
            // Update UI immediately if it's a dip
            if (result.changePercent < 0) {
              setStockData(prevData => {
                const newData = [...prevData, result].sort((a, b) => a.changePercent - b.changePercent);
                return newData;
              });
            }
          }
        });
        
        setProgress(prev => ({ ...prev, loaded: Math.min(i + batchSize, stocksToScan.length) }));
        
        // Delay between batches (respect rate limits)
        if (i + batchSize < stocksToScan.length) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
      
      // Final sort of all results
      const allDips = results.filter(r => r.changePercent < 0).sort((a, b) => a.changePercent - b.changePercent);
      setStockData(allDips);
      
      setLastUpdated(new Date().toLocaleTimeString());
      setScanComplete(true);
      
      if (allDips.length === 0) {
        setError(`🎉 No significant dips found! Market is having a strong day. Scanned ${results.length} stocks.`);
      }
      
    } catch (error) {
      setError(`Scan error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - function is self-contained

  const filteredData = stockData.filter(stock => {
    const matchesSearch = stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         stock.name.toLowerCase().includes(searchTerm.toLowerCase());
    const meetsMinDrop = Math.abs(stock.changePercent) >= minDrop;
    return matchesSearch && meetsMinDrop;
  });

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <TrendingDown className="w-8 h-8 text-red-500" />
                DipDetect
              </h1>
              <p className="text-gray-600 mt-1">Live US stock dip scanner - Finding the biggest drops</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Last updated</div>
              <div className="font-medium">{lastUpdated || 'Not scanned yet'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Min Drop:</label>
                <select 
                  value={minDrop} 
                  onChange={(e) => setMinDrop(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={0.5}>0.5%+</option>
                  <option value={1}>1%+</option>
                  <option value={2}>2%+</option>
                  <option value={5}>5%+</option>
                  <option value={10}>10%+</option>
                </select>
              </div>

              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search stocks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <button 
              onClick={scanMarket}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              {loading ? 'Scanning...' : 'Scan Market'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Progress */}
        {loading && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-800">Scanning 50 stocks...</span>
            </div>
            <div className="flex justify-between text-sm text-blue-700 mb-2">
              <span>Progress: {progress.loaded}/{progress.total}</span>
              <span>{progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="h-2 bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${progress.total > 0 ? (progress.loaded / progress.total) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Status */}
        {error && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-800">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Results Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            🔥 Biggest Market Dips ({filteredData.length} found)
          </h2>
          {scanComplete && filteredData.length === 0 && !error && (
            <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4" />
              <span>Great news! No major dips detected. The market is performing well today.</span>
            </div>
          )}
          {!scanComplete && !loading && stockData.length === 0 && (
            <div className="flex items-center gap-2 text-gray-600 bg-gray-50 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4" />
              <span>Click "Scan Market" to find the biggest stock dips right now.</span>
            </div>
          )}
        </div>

        {/* Results Table */}
        {filteredData.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-red-50 to-orange-50 border-b">
              <div className="flex items-center justify-between">
                <span className="font-medium text-red-800">🚨 Live Dip Results (Ranked by Drop %)</span>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-800 font-medium">
                      {filteredData.filter(s => s.cached).length} Cached
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 font-medium">
                      {filteredData.filter(s => !s.cached).length} Fresh
                    </span>
                  </div>
                  <span className="text-xs text-red-600">{loading ? 'Updating...' : 'Complete'}</span>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drop</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Volume</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">52W Dip</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cache</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((stock, index) => (
                    <tr key={stock.symbol} className={`hover:bg-gray-50 ${index < 3 ? 'bg-red-25' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          index === 0 ? 'bg-red-500 text-white' :
                          index === 1 ? 'bg-orange-500 text-white' :
                          index === 2 ? 'bg-yellow-500 text-white' :
                          'bg-gray-300 text-gray-700'
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-bold text-gray-900">{stock.symbol}</div>
                          <div className="text-xs text-gray-500 max-w-32 truncate">{stock.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${stock.price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-red-600">
                          {stock.changePercent.toFixed(2)}%
                        </div>
                        <div className="text-xs text-red-500">
                          ${stock.change.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(stock.volume / 1000000).toFixed(1)}M
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          stock.dipFrom52w < -50 ? 'bg-red-100 text-red-800' :
                          stock.dipFrom52w < -30 ? 'bg-orange-100 text-orange-800' :
                          stock.dipFrom52w < -15 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {stock.dipFrom52w.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          T{stock.tier}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {stock.cached ? (
                          <div className="flex flex-col items-start">
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                              Cached
                            </span>
                            {stock.cacheAge !== null && (
                              <span className="text-xs text-gray-500 mt-1">
                                {stock.cacheAge}s ago
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Fresh
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Market Summary */}
        {scanComplete && (
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="font-medium text-gray-800 mb-2">📊 Market Summary</h3>
            <div className="text-sm text-gray-600">
              <p>• Scanned 50 top US stocks across 6 tiers</p>
              <p>• Found {filteredData.length} stocks with drops ≥ {minDrop}%</p>
              <p>• Data from Alpha Vantage - {lastUpdated}</p>
              {filteredData.length > 0 && (
                <p>• Cache: {filteredData.filter(s => s.cached).length} results from cache (5min TTL), {filteredData.filter(s => !s.cached).length} fresh from API</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DipDetectApp;
